import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FocusTrap from 'focus-trap-react';
import { useIntl } from 'react-intl';
import { differenceInCalendarDays, format, isValid, parse } from 'date-fns';
import { DayPicker, useInput } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { CloseButton, EditButton } from '#/shared/components/PickerButtons';
import useManagePopperVisibility from '#/trip-planner/hooks/useManagePopperVisibility';
import useIntlDatetime from '#/shared/hooks/useIntlDatetime';
import * as Styled from './styled';

function DatePicker({
  selectedDate,
  onDateChange,
  dateFormat,
  defaultDate,
  fromDate,
  toDate,
  today,
  inputId,
  className,
}) {
  const { dayPickerProps, inputProps } = useInput({
    defaultSelected: defaultDate,
    format: dateFormat,
    fromDate,
    toDate,
    required: true,
  });
  const { onMonthChange } = dayPickerProps;

  const getLastValidDateString = () =>
    format(isValid(selectedDate) ? selectedDate : defaultDate, dateFormat);

  const [inputValue, setInputValue] = useState(getLastValidDateString());
  const [inputHasFocus, setInputHasFocus] = useState(false);

  const intl = useIntl();
  const { dateFnsLocale } = useIntlDatetime();

  useEffect(() => {
    if (!inputHasFocus) {
      setInputValue(
        format(selectedDate, dateFormat, { locale: dateFnsLocale })
      );
    }
  }, [inputHasFocus, selectedDate, dateFormat, dateFnsLocale]);

  const {
    popperRef,
    fieldRef,
    inputRef,
    popperVisible,
    showPopper,
    hidePopper,
    onFieldClick,
    focusTrapOptions,
  } = useManagePopperVisibility({
    onPopperShow: () => {
      // ensure calendar is on page with selected date when opened
      onMonthChange(selectedDate);
    },
  });

  // use of this method gives the user some leeway in how they enter
  // separators between date parts
  const normalizeDateString = dateString =>
    dateString.split(/[,\s/-]+/).join(' ');

  const [inputValueDirty, setInputValueDirty] = useState(false);

  const onInputBlur = e => {
    setInputHasFocus(false);

    const date = parse(
      normalizeDateString(e.target.value),
      normalizeDateString(dateFormat),
      new Date(),
      { locale: dateFnsLocale }
    );

    if (
      inputValueDirty &&
      isValid(date) &&
      (!fromDate || differenceInCalendarDays(date, fromDate) >= 0) &&
      (!toDate || differenceInCalendarDays(toDate, date) >= 0)
    ) {
      onDateChange(date);

      // move calendar to page with new date selection
      onMonthChange(date);
    } else {
      setInputValue(getLastValidDateString());
    }

    setInputValueDirty(false);
  };

  return (
    <Styled.DatePickerContainer className={className}>
      <Styled.DateFieldContainer
        onClick={onFieldClick}
        ref={fieldRef}
        popperVisible={popperVisible}
      >
        <Styled.DateInput
          type="text"
          value={inputValue}
          onFocus={() => setInputHasFocus(true)}
          onChange={e => {
            setInputValue(e.target.value);
            setInputValueDirty(true);
          }}
          onBlur={onInputBlur}
          placeholder={inputProps.placeholder}
          ref={inputRef}
          id={inputId}
        />
        <EditButton
          Icon={Styled.CalendarIcon}
          onClick={showPopper}
          buttonAriaLabelIntlId="planner.date-picker-aria"
          iconAriaLabelIntlId="planner.calendar-icon-aria"
        />
      </Styled.DateFieldContainer>
      {popperVisible && (
        <FocusTrap focusTrapOptions={focusTrapOptions}>
          <Styled.PopperWrapper>
            <Styled.PopperContainer
              ref={popperRef}
              role="dialog"
              aria-modal
              aria-label={intl.formatMessage({
                id: 'planner.date-picker-aria',
              })}
            >
              <DayPicker
                initialFocus={popperVisible}
                mode="single"
                selected={selectedDate}
                onSelect={date => {
                  if (isValid(date)) onDateChange(date);
                }}
                fromDate={fromDate}
                toDate={toDate}
                locale={dateFnsLocale}
                onMonthChange={onMonthChange}
                month={dayPickerProps.month}
                onDayClick={dayPickerProps.onDayClick}
                today={today || dayPickerProps.today}
              />
              <CloseButton onClick={hidePopper} />
            </Styled.PopperContainer>
          </Styled.PopperWrapper>
        </FocusTrap>
      )}
    </Styled.DatePickerContainer>
  );
}

DatePicker.propTypes = {
  /** Date that will appear as selected in input and on calendar */
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  /** Handler fired when date changes, is pass the next date as a param */
  onDateChange: PropTypes.func.isRequired,
  /** Date format used in input, accepts valid values from Unicode TR35 */
  dateFormat: PropTypes.string,
  /** Date used as selection if empty and invalid selected date is supplied */
  defaultDate: PropTypes.instanceOf(Date),
  /** Minimum date that can be selected */
  fromDate: PropTypes.instanceOf(Date),
  /** Maximum date that can be selected */
  toDate: PropTypes.instanceOf(Date),
  /** The date to be presented as the current day */
  today: PropTypes.instanceOf(Date),
  /** ID for this component's input element */
  inputId: PropTypes.string,
  /** Pass-through to allow styling by styled-components */
  className: PropTypes.string,
};

DatePicker.defaultProps = {
  dateFormat: 'P',
  defaultDate: new Date(),
  fromDate: undefined,
  toDate: undefined,
  today: undefined,
  inputId: undefined,
  className: null,
};

export default DatePicker;
export { Styled };
